import { toast } from '@systemeio/ui'
import LinkWithoutPrefetch from 'shared/components/link-without-prefetch'
import TransLoco from 'shared/components/trans-loco'
import { RequestModuleEnum } from 'shared/enums/request-module-enum'
import { useControlledSwr } from 'shared/hooks/use-controlled-swr'
import { useFetch } from 'shared/hooks/use-fetch'
import { RequestMethodsEnum } from 'shared/hooks/use-fetch/types'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { useRevalidateModules } from 'shared/hooks/use-revalidate-modules'
import useUser from 'shared/hooks/use-user'
import { GOOGLE_INTEGRATIONS_PATH } from '../api'
import { CalendarAccessRoleEnum, CalendarInterface } from '../types'

export const useCalendars = () => {
  const { user } = useUser()
  const { t } = useLocoTranslation()
  const { revalidateModules } = useRevalidateModules()
  const { fetcher: fetchIntegrations } = useFetch<RequestMethodsEnum.get, CalendarInterface[]>({
    method: RequestMethodsEnum.get,
    accessDeniedHandler: () => {
      toast.error(
        <TransLoco
          t={t}
          i18nKey="dashboard.integrations.google_calendar.disconnected_info"
          components={{
            a: <LinkWithoutPrefetch href={'/integrations'} className="primary-link" />,
          }}
        />,
      )
      revalidateModules([RequestModuleEnum.GoogleIntegrations])
    },
  })
  const { fetcher: patchFetcher } = useFetch<RequestMethodsEnum.patch>({
    method: RequestMethodsEnum.patch,
  })

  const { data, mutate, isLoading } = useControlledSwr(
    () => {
      if (!user) return
      return [
        `${GOOGLE_INTEGRATIONS_PATH}/calendars?minAccessRole=${CalendarAccessRoleEnum.FreeBusyReader}`,
        RequestModuleEnum.GoogleIntegrationsCalendars,
      ]
    },
    fetchIntegrations,
    undefined,
  )

  const patchCalendar = async (
    calendarId: string,
    integrationId: string,
    changes: Partial<{
      useForCheck: boolean
      useForSync: boolean
    }>,
  ) => {
    await patchFetcher(
      `${GOOGLE_INTEGRATIONS_PATH}/calendars/${encodeURIComponent(
        calendarId,
      )}?integrationId=${integrationId}`,
      changes,
    )
  }

  const hasGoogleCalendars = data ? data.length > 0 : false

  return {
    data,
    mutate,
    patchCalendar,
    hasGoogleCalendars,
    isLoading,
  }
}
